import Vue from "vue";
import * as Sentry from "@sentry/vue";
import router from "./router";

const EDURIO_SENTRY_APP_KEY = 'edurio-application-key'
if (window.environment !== 'local') {
    Sentry.init({
        Vue,
        dsn: 'https://777d6c147b5d4577bf5c7ea5c42d0626@sentry.io/1531875',
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.browserProfilingIntegration(),
            Sentry.replayIntegration(),
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: [EDURIO_SENTRY_APP_KEY],
                behaviour: "drop-error-if-contains-third-party-frames"
            })
        ],
        attachProps: true,
        logErrors: true,
        trackComponents: true,

        tracesSampleRate: window.environment === 'production' ? 0.01 : 0.1,
        tracePropagationTargets: [
            "localhost",
            /^https:\/\/.*\.edurio\.com/
        ],

        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 0.1,

        profilesSampleRate: window.environment === 'production' ? 0.01 : 0.1,

        environment: window.environment,
    });
}

import App from "./App.vue";
import store from "./store";
import VueEvents from "vue-events";
import * as VueLocale from "./locale/VueLocale.js";

Vue.use(VueEvents);

import VueResource from "vue-resource";
Vue.use(VueResource);

var VueFilter = require("vue-filter");
Vue.use(VueFilter);

import VueForm from "vue-form";
Vue.use(VueForm);

import Vuebar from "vuebar";
Vue.use(Vuebar);

import PortalVue from "portal-vue";
Vue.use(PortalVue);

window.Vue = Vue;
require("vue-events");

import moment from "moment";

import globals from "./helpers/globals";

Vue.prototype.$globals = globals;

import timeout from "@/mixins/timeout";
Vue.mixin({
    methods: {
        textend(value, times) {
            return new Array(times).fill(value).join('')
        },
        getNestedObject(nestedObj, pathArr) {
            return pathArr.reduce((obj, key) =>
                (obj && obj[key] !== 'undefined') ? obj[key] : undefined, nestedObj);
        },
        checkPermissionAndRedirect(permission) {
            const areUserPermissionsLoaded = this.$store.state.user.user.loaded
            if (!areUserPermissionsLoaded) {
                setTimeout(() => this.checkPermissionAndRedirect(permission),1500)
                return;
            }

            if (!this.$store.state.user.user.permissions[permission]) {
                window.location.href = '/dashboard#forbidden'
            }
        },
        // #3108 Return boolean if identity has the permission.
        perm(path) {
            let pathArr = path.split(".");
            let permission = this.getNestedObject(this.$store.state.permissions.permissions, pathArr);
            let hasPerm = typeof permission !== 'undefined' && permission !== null && permission !== false;
            return hasPerm;
        },
        // #4053 Call back the method when permissions are loaded.
        whenPermsAreLoaded(funcToCallWhenReady) {
            if (this.arePermsLoaded()) {
                return funcToCallWhenReady();
            } else {
                // #4053 Wait for a while, then try again.
                setTimeout(function() {
                    return funcToCallWhenReady();
                }.bind(this), 1500)
            }
        },
        // #4053 Meant for places that requires permissions to be loaded.
        arePermsLoaded() {
            return this.perm('list');
        },
        // #3752 Added to check if benchmark has a deep property called `answer_percent`.
        hasProp(nestedObj, pathArr) {
            let obj = this.getNestedObject(nestedObj, pathArr);
            return typeof obj !== 'undefined' && obj !== null && obj !== false;
        },
        // #3875 Inform the client and us about the error.
        log(type, anything, description = "") {
            // #3875 TODO: Use translations. `this.$options.filters.lang('general.saved')`.
            addEdNotification('Sorry, something went wrong.', 'error');
            this.$http.post(window.endpoint_ish + '/api/log', { "type": type, "data": JSON.stringify([anything, window.navigator.userAgent]), "description": description });
        },
        checkValidEmail(email) {
            return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(email)
        },
        checkValidUrl(url) {
            const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
                '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
                '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
                '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
                '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
                '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
            return !!pattern.test(url);
        },
        checkValidDate(date) {
            return moment(date).isValid();
        },
        getCookie(cname) {
            let name = cname + "=";
            let decodedCookie = decodeURIComponent(document.cookie);
            let ca = decodedCookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) == ' ') {
                    c = c.substring(1);
                }
                if (c.indexOf(name) == 0) {
                    return c.substring(name.length, c.length);
                }
            }
            return "";
        },
        openModal(modalData) {
            this.$store.commit('SET_ACTIVE_MODAL', modalData)
        },
        closeOpenModal() {
            this.$store.commit('CLOSE_MODAL');
        },
        copyToClipboard(evt) {
            let offsetY = window.pageYOffset;
            let textContent = evt.textDirectly || evt.target.innerText;

            // create hidden text element, if it doesn't already exist
            let targetId = "_hiddenCopyText_";

            // must use a temporary form element for the selection and copy
            let target = document.createElement("textarea");
            target.style.position = "absolute";
            target.style.left = "-9999px";
            target.id = targetId;
            document.body.appendChild(target);

            target.textContent = textContent;

            // select the content
            target.focus();
            target.setSelectionRange(0, target.value.length);

            // copy the selection
            let succeed;
            try {
                succeed = document.execCommand("copy");
                evt.target.classList.add('successful-copy');

                setTimeout(() => {
                    evt.target.classList.remove('successful-copy');
                }, 1000)
            } catch (e) {
                succeed = false;
            }

            $(window).scrollTop(offsetY);

            // remove temporary textarea
            target.remove();

            return succeed;
        },
        isWhitespaceOrEmpty(text = '') {
            return !/[^\s]/.test(text)
        },
        initNewSuperadminUxLayout(enableNewLayoutStyling = true) {
            const mainContainer = $("body > .container");
            const attrName = 'new-design-instances';
            let newDesignInstances = mainContainer.attr(attrName) || 0;

            mainContainer.attr(attrName, enableNewLayoutStyling ? ++newDesignInstances : --newDesignInstances)

            const shouldKeepStyling = enableNewLayoutStyling || newDesignInstances > 0;
            const styling =
                shouldKeepStyling ?
                    {
                        'padding': 0,
                        'border-top-left-radius': '6px',
                        'border-top-right-radius': '6px',
                        'font-family': 'Roboto,sans-serif',
                    } :
                    {
                        'padding': '20px 40px',
                        'border-top-left-radius': 0,
                        'border-top-right-radius': 0
                    }

            mainContainer.css(styling)
        },
        s2ab(s) {
            const buf = new ArrayBuffer(s.length);
            const view = new Uint8Array(buf);
            for (let i = 0; i != s.length; ++i)
                view[i] = s.charCodeAt(i) & 0xff;
            return buf;
        },
        timeout,
        triggerModalCallback(arg) {
            this.$store.dispatch('triggerModalCallback', arg);
        },
        confirmAction(confirmText) {
            return new Promise(resolve => {
                this.openModal(
                    {
                        modal: 'SuperAdminAcceptModal',
                        passedProps: {
                            confirmText,
                        },
                        callback: () => resolve(true),
                        callbackCancel: () => resolve(false)
                    });
            })
        },

        whenTokenIsLoaded(funcToCallWhenLoaded) {
            let tokenChecker = setInterval(function() {
                if (window.token_loaded) {
                    clearInterval(tokenChecker);
                    return funcToCallWhenLoaded();
                }
            }.bind(this), 100);
        },
    },
});

sessionStorage.setItem("language", window.current_language)
var Authorization = require("./authorization/Authorization.js");

Vue.filter("stripHTML", (value) => {
    let div = document.createElement("div");
    div.innerHTML = value;
    return div.textContent || div.innerText || "";
});

Vue.http.options.root = window.api_endpoint;
Vue.http.interceptors.push(
    require("./interceptors/CacheHeadersInterceptor.js").intercept
);
Vue.http.interceptors.push(Authorization.intercept);
Vue.http.interceptors.push(
    require("./interceptors/LanguageInterceptor.js").intercept
);
Vue.http.interceptors.push(
    require("./interceptors/SessionTypeInterceptor.js").intercept
);

var Localization = require("./mixins/Localization.js");
Vue.mixin(Localization);
var Http = require("./mixins/Http.js");
Vue.mixin(Http);

// Global components
import Snackbar from 'vuejs-snackbar';
Vue.component('snackbar', Snackbar);

Vue.directive('click-outside', {
    bind: function(el, binding, vNode) {
        const bubble = binding.modifiers.bubble;
        const skipList = ["open-modal-btn"];
        const handler = (e) => {
            for (let cls of skipList) {
                if (e.target.classList.contains(cls)) {
                    return;
                }
            }
            if (bubble || (!el.contains(e.target) && el !== e.target)) {
                binding.value(e);
            }
        };
        el.__vueClickOutside__ = handler;

        setTimeout(() => {
            document.addEventListener('mousedown', handler)
        }, 100)
    },

    unbind: function(el, binding) {
        document.removeEventListener('mousedown', el.__vueClickOutside__)
        el.__vueClickOutside__ = null
    }
})

import * as action_types from "./store/action-types";

export let app = new Vue({
    store,
    router,
    data: {
        devmode: false,
        session: {
            has: false,
            message: false,
            locked: false
        },
        languages: {
            list: [],
            name: null,
            current: null
        },
        urls: {
            organisation: false
        },
        organisation: {
            is: false,
            id: null,
            name: null,
            year: null,
            type: null
        },
        identity: {
            id: null,
            solo: false,
            independent: false
        },
        login_token: null
    },
    computed: {
        user() {
            return this.$store.state.user.user;
        }
    },
    created() {
        this.session.has = window.session_has_message;
        this.session.message = window.session_message;
        this.languages.list = window.language_list;
        this.languages.name = window.current_language_name;
        this.languages.current = window.current_language;
        this.urls.organisation = window.url_organisation;
        this.devmode = window.devmode;

        this.organisation.is = window.is_org;
        this.organisation.id = window.school_id;
        this.organisation.name = window.school_name;
        this.organisation.year = window.school_year;
        this.organisation.type = window.school_type;
        this.organisation.teacher = window.school_teacher;
        this.identity.solo = window.identity_solo;
        this.identity.independent = window.is_independent;

        try {
            const token = JSON.parse(localStorage.getItem('token'));
            if (token) {
                const tokenBody = JSON.parse(atob(token.split('.')[1]));
                if (tokenBody && tokenBody.identity) {
                    this.identity.id = tokenBody.identity.id;
                }
                this.session.locked = !!tokenBody.locked;
            }
        } catch (e) {
            console.warn('Failed to parse identity from token', e);
        }

        this.login_token = window.login_token;

        // #3108 Load processed permissions. Data collected here will be used in perm(path) funciton.
        this.$store.dispatch(action_types.PERMISSION_LOAD);

        // Load the current user with perms from Vuex global storage
        this.$store.dispatch(action_types.USER_LOAD, {
            is_solo: this.identity.solo
        });

        window.addEdNotification = (msg, type, cb) => {
            this.$events.emit("alert", { msg: msg, type: type }, cb);
        };

        if (typeof this.$root.login_token !== 'undefined' && this.$root.login_token != null && this.$root.login_token != "") {
            localStorage.setItem("token", JSON.stringify(this.$root.login_token));
            window.location.href = "/dashboard";
        }
    },
    events: {
        "auth.token.set": function(token) {
            this.setLocal("token", token);
            Authorization.token = token;
            this.$emit("auth.token.new", token);
        },
        "message.notify.*": function(message) {
            this.$broadcast("message.notify.*", message);
        }
    },
    methods: {
        getLocal: function(key) {
            if (localStorage.getItem(key))
                return JSON.parse(localStorage.getItem(key));
            return null;
        },

        setLocal: function(key, value) {
            localStorage.setItem(key, JSON.stringify(value));
        },

        example: function() {
            console.log(this);
        },

        addBodyClass(name) {
            document.body.classList.add(name);
        },

        removeBodyClass(name) {
            document.body.classList.remove(name);
        }
    },
    watch: {
        '$route'(to, from) {
            document.title = to.meta.title || 'Edurio'
        }
    },
    mounted() {
        this.$events.listen("auth.token.set", token => {
            this.setLocal("token", token);
            Authorization.token = token;
        });
    },
    render: h => h(App)
})

Vue.filter("lang", VueLocale.lang)
VueLocale.loadLanguage(window.current_language)
    .then(() => {
        console.log("Translations set! Mounting vue...")
        app.$mount("#app")
    })
    .catch(e => {
        console.error("Error setting translations", e)
        app.$mount("#app")
    })

window.currentInstance = app
